import type { ReactNode, RefObject } from 'react'
import { useTranslation } from '@w/fe-inv-i18n'
import { Alert, AlertDescription, AlertTitle } from '@w/fe-ui/components/alert'
import { Button } from '@w/fe-ui/components/button'
import { ErrorBoundary } from '@w/fe-ui/system/error-boundary'
import {
  type RouterHolder,
  useAuthSession,
  useResumeOnSignIn,
  useRoutesAuthSession,
} from './use-auth-session'

/** Shown while a refresh failed without a refusal: the session is kept and the app keeps trying */
export function ConnectionProblemNotice() {
  const { t } = useTranslation('platform')
  return (
    <Alert data-testid="auth-connection-problem">
      <AlertDescription>{t('session.connectionProblem')}</AlertDescription>
    </Alert>
  )
}

interface SessionEndedNoticeProps {
  /** Title and button only, for a widget: a host page may embed several */
  compact?: boolean
}

/** Shown instead of the content once the session ended in a tab that had one */
export function SessionEndedNotice({ compact = false }: SessionEndedNoticeProps) {
  const { t } = useTranslation('platform')
  const { session } = useAuthSession()
  return (
    <Alert data-testid="auth-session-ended">
      <AlertTitle>{t('session.ended.title')}</AlertTitle>
      <AlertDescription>
        {!compact && <p>{t('session.ended.description')}</p>}
        <Button data-testid="auth-session-ended-sign-in" onClick={session.goToLogin}>
          {t('session.ended.signIn')}
        </Button>
      </AlertDescription>
    </Alert>
  )
}

/**
 * The shell's error boundary, aware of the auth session; it resets once the failed queries were
 * refetched. A host page may embed several widgets, each with a shell of its own, so an ended
 * session shows compactly here, and the connection notice only in AuthSessionRoutesFrame.
 */
export function AuthSessionBoundary({ children }: { children: ReactNode }) {
  const { status, recoveries, session } = useAuthSession()
  useResumeOnSignIn(session, status)
  if (status === 'session-ended') return <SessionEndedNotice compact />
  return <ErrorBoundary resetKeys={[recoveries]}>{children}</ErrorBoundary>
}

interface AuthSessionRoutesFrameProps {
  routerRef: RefObject<RouterHolder | null>
  children: ReactNode
}

/**
 * Around the invoicing app's router: the connection notice above the routes, the full ended
 * notice instead of them. Outside the shell: a root route that failed renders its error outside it.
 */
export function AuthSessionRoutesFrame({ routerRef, children }: AuthSessionRoutesFrameProps) {
  const status = useRoutesAuthSession(routerRef)
  if (status === 'session-ended') return <SessionEndedNotice />
  return (
    <>
      {status === 'connection-problem' && <ConnectionProblemNotice />}
      {children}
    </>
  )
}
